// Ghost-blueprint render (CLS-1/2, M37): the instructor's compiled blueprint drawn
// as translucent phantom voxels — a client overlay, never real blocks. Two looks
// toggle: 'ghost' (uniform blueprint-cyan — "here is what to build") and 'heat'
// (green = correct, red = wrong, faint = still missing). One InstancedMesh of unit
// cubes: the matrix rebuilds only when the cell set changes, colours refresh each
// diff. Client-only (ENG-1); the sim owns the plan + the placed-vs-plan status.
import * as THREE from 'three';
import type { CellStatus, GhostCell } from '../../sim/education/classroom';

export type HeatMode = 'ghost' | 'heat';

const GHOST = new THREE.Color(0x53b7ff);   // blueprint cyan
const CORRECT = new THREE.Color(0x46d15a); // green
const WRONG = new THREE.Color(0xe8433a);   // red
const MISSING = new THREE.Color(0x9fb3c8); // faint slate

function colorFor(status: CellStatus, mode: HeatMode, out: THREE.Color): THREE.Color {
  if (mode === 'ghost') return out.copy(GHOST);
  return out.copy(status === 'correct' ? CORRECT : status === 'wrong' ? WRONG : MISSING);
}

export class BlueprintRenderer {
  private readonly group = new THREE.Group();
  private mesh: THREE.InstancedMesh | null = null;
  private readonly geom = new THREE.BoxGeometry(1, 1, 1);
  private readonly mat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.32, depthWrite: false });
  /** Positional signature so we rebuild instances only when the cell set changes. */
  private sig = '';
  visible = true;

  constructor(scene: THREE.Scene) { scene.add(this.group); }

  setVisible(v: boolean): void { this.visible = v; this.group.visible = v; }

  /** Rebuild (on a cell-set change) + recolour the overlay for the current diff. */
  update(cells: readonly GhostCell[], mode: HeatMode): void {
    const sig = cells.length === 0 ? '' : `${cells.length}:${cells[0]!.x},${cells[0]!.y},${cells[0]!.z}|${cells[cells.length - 1]!.x},${cells[cells.length - 1]!.y},${cells[cells.length - 1]!.z}`;
    if (sig !== this.sig || (cells.length > 0 && !this.mesh)) { this.rebuild(cells); this.sig = sig; }
    const mesh = this.mesh;
    if (!mesh) return;
    const c = new THREE.Color();
    for (let i = 0; i < cells.length; i++) mesh.setColorAt(i, colorFor(cells[i]!.status, mode, c));
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }

  private rebuild(cells: readonly GhostCell[]): void {
    if (this.mesh) { this.group.remove(this.mesh); this.mesh.dispose(); this.mesh = null; }
    if (cells.length === 0) return;
    const mesh = new THREE.InstancedMesh(this.geom, this.mat, cells.length);
    const m = new THREE.Matrix4();
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i]!;
      m.makeTranslation(cell.x + 0.5, cell.y + 0.5, cell.z + 0.5); // block (x,y,z) → cube centre
      mesh.setMatrixAt(i, m);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.frustumCulled = false;
    this.mesh = mesh;
    this.group.add(mesh);
  }

  clear(): void {
    if (this.mesh) { this.group.remove(this.mesh); this.mesh.dispose(); this.mesh = null; }
    this.sig = '';
  }
}
